import { useRef, useState, useEffect, useCallback } from "react";
import { IconStarBurst } from "@/components/ui/cartoon-icons";

interface LazyVideoProps {
  src: string;
  className?: string;
  onClick?: () => void;
}

export function LazyVideo({ src, className, onClick }: LazyVideoProps) {
  const ref = useRef<HTMLVideoElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible && ref.current) {
      ref.current.load();
    }
  }, [isVisible, src]);

  const handleCanPlay = useCallback(() => {
    setLoading(false);
    const el = ref.current;
    if (!el) return;
    el.play().catch(() => {});
  }, []);

  const videoSrc = isVisible ? `${src}#t=0.1` : undefined;

  return (
    <div className="relative w-full h-full">
      {loading && isVisible && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#ece7f8] z-10">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-[#b8a5f0]/20 blur-[20px] animate-pulse-glow" style={{ width: 56, height: 56, margin: "-8px" }} />
            <div className="size-10 rounded-full bg-[#241d3a]/[0.06] border border-[#241d3a]/15 flex items-center justify-center animate-pulse-ring">
              <IconStarBurst className="size-4 text-[#7c5cf0] animate-float" />
            </div>
          </div>
        </div>
      )}
      <video
        ref={ref}
        src={videoSrc}
        className={className}
        muted
        autoPlay
        loop
        playsInline
        preload={isVisible ? "auto" : "none"}
        onCanPlay={handleCanPlay}
        onClick={(e) => {
          if (onClick) {
            e.preventDefault();
            e.stopPropagation();
            onClick();
          }
        }}
        style={{ cursor: onClick ? "pointer" : undefined }}
      />
    </div>
  );
}
